import { Sliders } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient.ts';
import './AdvancedSearch.css';

const AdvancedSearch = ({ isOpen, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Filters
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [petType, setPetType] = useState('all');

  const navigate = useNavigate();
  const searchRef = useRef(null);

  // ─── Core search function ────────────────────────────────────────────────────
  // Accepts fresh category/subcategory data directly to avoid stale-closure bugs
  const searchProductsWithData = useCallback(async ({
    query = searchQuery,
    cats = categories,
    subs = subcategories,
    selCats = selectedCategories,
    selSubs = selectedSubcategories,
    selBrands = selectedBrands,
    pet = petType,
  } = {}) => {
    setLoading(true);

    try {
      const productIds = new Set();
      const searchTerm = query.trim().toLowerCase();

      const hasFilters =
        searchTerm ||
        selCats.length > 0 ||
        selSubs.length > 0 ||
        selBrands.length > 0 ||
        pet !== 'all';

      // ── No filters: show recent products ──────────────────────────────────
      if (!hasFilters) {
        const { data, error } = await supabase
          .from('products')
          .select('*')
          .limit(50)
          .order('created_at', { ascending: false });

        if (!error && data) {
          const shuffled = data.sort(() => 0.5 - Math.random());
          setFilteredProducts(shuffled.slice(0, 20));
        }
        setLoading(false);
        return;
      }

      // ── STEP 1: Query products table ──────────────────────────────────────
      let mainQuery = supabase.from('products').select('*');

      if (searchTerm) {
        mainQuery = mainQuery.or(
          `name.ilike.%${searchTerm}%,` +
          `description.ilike.%${searchTerm}%,` +
          `brand.ilike.%${searchTerm}%,` +
          `category.ilike.%${searchTerm}%,` +
          `subcategory.ilike.%${searchTerm}%,` +
          `weight.ilike.%${searchTerm}%,` +
          `price_range.ilike.%${searchTerm}%,` +
          `composition.ilike.%${searchTerm}%,` +
          `ingredients.ilike.%${searchTerm}%,` +
          `analysis.ilike.%${searchTerm}%,` +
          `antioxidants.ilike.%${searchTerm}%,` +
          `energy.ilike.%${searchTerm}%`
        );
      }

      const { data: allProducts, error: allError } = await mainQuery.limit(500);

      if (allError) console.error('❌ Error fetching products:', allError);

      // ── STEP 2: Apply category / subcategory / brand filters in memory ────
      const applyFilters = (products) => {
        let result = products || [];

        // Category filter
        if (selCats.length > 0) {
          const catNames = cats
            .filter(c => selCats.includes(c.id))
            .map(c => c.name.toLowerCase().trim());

          result = result.filter(product => {
            try {
              const pCats = JSON.parse(product.category || '[]');
              return catNames.some(cn =>
                pCats.some(pc => pc.toLowerCase().trim() === cn)
              );
            } catch { return false; }
          });
        } else if (pet !== 'all') {
          const catNames = cats
            .filter(c => c.pet_type === pet)
            .map(c => c.name.toLowerCase().trim());

          result = result.filter(product => {
            try {
              const pCats = JSON.parse(product.category || '[]');
              return catNames.some(cn =>
                pCats.some(pc => pc.toLowerCase().trim() === cn)
              );
            } catch { return false; }
          });
        }

        // Subcategory filter
        if (selSubs.length > 0) {
          const subNames = subs
            .filter(s => selSubs.includes(s.id))
            .map(s => s.name.toLowerCase().trim());

          result = result.filter(product => {
            try {
              const pSubs = JSON.parse(product.subcategory || '[]');
              return subNames.some(sn =>
                pSubs.some(ps => ps.toLowerCase().trim() === sn)
              );
            } catch { return false; }
          });
        }

        // Brand filter
        if (selBrands.length > 0) {
          result = result.filter(p => selBrands.includes(p.brand));
        }

        return result;
      };

      applyFilters(allProducts).forEach(p => productIds.add(p.id));

      // ── STEP 3: Search in product_variations (only when there's a term) ───
      if (searchTerm) {
        const { data: variations, error: varError } = await supabase
          .from('product_variations')
          .select('product_id')
          .or(
            `variation_name.ilike.%${searchTerm}%,` +
            `weight.ilike.%${searchTerm}%,` +
            `composition.ilike.%${searchTerm}%,` +
            `price.ilike.%${searchTerm}%,` +
            `ingredients.ilike.%${searchTerm}%,` +
            `analysis.ilike.%${searchTerm}%,` +
            `antioxidants.ilike.%${searchTerm}%,` +
            `energy.ilike.%${searchTerm}%`
          );

        if (varError) console.error('❌ Variations search error:', varError);

        if (variations?.length > 0) {
          const { data: varProducts } = await supabase
            .from('products')
            .select('*')
            .in('id', variations.map(v => v.product_id));

          applyFilters(varProducts).forEach(p => productIds.add(p.id));
        }
      }

      // ── STEP 4: Fetch final deduplicated product list ─────────────────────
      if (productIds.size > 0) {
        const { data: finalProducts, error: finalError } = await supabase
          .from('products')
          .select('*')
          .in('id', Array.from(productIds))
          .order('created_at', { ascending: false });

        if (finalError) console.error('❌ Error fetching final products:', finalError);

        setFilteredProducts(finalProducts || []);
      } else {
        setFilteredProducts([]);
      }
    } catch (error) {
      console.error('❌ Search error:', error);
      setFilteredProducts([]);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, categories, subcategories, selectedCategories, selectedSubcategories, selectedBrands, petType]);

  // ─── Fetch brands (once on open) ─────────────────────────────────────────────
  const fetchBrands = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('brand')
        .not('brand', 'is', null);

      if (!error && data) {
        const uniqueBrands = [...new Set(data.map(p => p.brand).filter(Boolean))].sort();
        setBrands(uniqueBrands);
      }
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  }, []);

  // ─── Fetch subcategories ──────────────────────────────────────────────────────
  const fetchSubcategories = useCallback(async (currentCategories, currentSelectedCats, currentPetType) => {
    try {
      let query = supabase.from('subcategories').select('*');

      if (currentSelectedCats.length > 0) {
        query = query.in('category_id', currentSelectedCats);
      } else if (currentPetType !== 'all' && currentCategories.length > 0) {
        const catIdsForPet = currentCategories
          .filter(c => c.pet_type === currentPetType)
          .map(c => c.id);
        if (catIdsForPet.length > 0) {
          query = query.in('category_id', catIdsForPet);
        }
      }

      const { data, error } = await query.order('order', { ascending: true });
      if (!error) setSubcategories(data || []);
    } catch (error) {
      console.error('Error fetching subcategories:', error);
    }
  }, []);


  useEffect(() => {
    if (!isOpen) return;

    const handlePetTypeChange = async () => {
      // Reset category/subcategory selections when pet type changes
      setSelectedCategories([]);
      setSelectedSubcategories([]);

      // 1. Fetch fresh categories for the new petType
      let query = supabase.from('categories').select('*');
      if (petType !== 'all') {
        query = query.eq('pet_type', petType);
      }
      const { data, error } = await query.order('order', { ascending: true });

      if (error) {
        console.error('Error fetching categories:', error);
        return;
      }

      const freshCategories = data || [];
      setCategories(freshCategories);

      // 2. Fetch subcategories with fresh data
      let subQuery = supabase.from('subcategories').select('*');
      if (petType !== 'all' && freshCategories.length > 0) {
        const catIds = freshCategories.map(c => c.id);
        subQuery = subQuery.in('category_id', catIds);
      }
      const { data: subData } = await subQuery.order('order', { ascending: true });
      const freshSubcategories = subData || [];
      setSubcategories(freshSubcategories);

      // 3. Search using freshly fetched data (bypasses stale state)
      await searchProductsWithData({
        query: searchQuery,
        cats: freshCategories,
        subs: freshSubcategories,
        selCats: [],       // reset
        selSubs: [],       // reset
        selBrands: selectedBrands,
        pet: petType,
      });
    };

    handlePetTypeChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [petType, isOpen]);

  // ─── Initial load (brands) ───────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      fetchBrands();
    }
  }, [isOpen, fetchBrands]);

  // ─── Subcategories update when selected categories change ────────────────────
  useEffect(() => {
    if (categories.length > 0) {
      fetchSubcategories(categories, selectedCategories, petType);
    }
  }, [selectedCategories, categories, petType, fetchSubcategories]);

  // ─── Debounced search for query / brand / category / subcategory changes ──────
  // petType is intentionally excluded here — handled by the dedicated effect above
  useEffect(() => {
    if (!isOpen) return;

    const timer = setTimeout(() => {
      searchProductsWithData();
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, selectedCategories, selectedSubcategories, selectedBrands, isOpen]);

  // ─── Handlers ────────────────────────────────────────────────────────────────
  const handleProductClick = (productId) => {
    navigate(`/shop-details/${productId}`);
    onClose();
  };

  const toggleCategory = (categoryId) => {
    setSelectedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
    setSelectedSubcategories([]);
  };

  const toggleSubcategory = (subcategoryId) => {
    setSelectedSubcategories(prev =>
      prev.includes(subcategoryId)
        ? prev.filter(id => id !== subcategoryId)
        : [...prev, subcategoryId]
    );
  };

  const toggleBrand = (brand) => {
    setSelectedBrands(prev =>
      prev.includes(brand)
        ? prev.filter(b => b !== brand)
        : [...prev, brand]
    );
  };

  const clearAllFilters = () => {
    setSelectedCategories([]);
    setSelectedSubcategories([]);
    setSelectedBrands([]);
    setPetType('all');
    setSearchQuery('');
  };

  // ─── Deduplicated lists for rendering ────────────────────────────────────────
  const uniqueCategories = React.useMemo(() => {
    const seen = new Set();
    return categories.filter(cat => {
      if (seen.has(cat.id)) return false;
      seen.add(cat.id);
      return true;
    });
  }, [categories]);

  const uniqueSubcategories = React.useMemo(() => {
    const seen = new Set();
    return subcategories.filter(sub => {
      if (seen.has(sub.id)) return false;
      seen.add(sub.id);
      return true;
    });
  }, [subcategories]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="search-backdrop" onClick={onClose} />

      {/* Advanced Search Modal */}
      <div className="advanced-search-modal">
        {/* Header */}
        <div className="search-header">
          <h2></h2>
          <button className="close-btn" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Main Search Input */}
        <div className="search-input-container">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (searchQuery.trim()) {
                const params = new URLSearchParams();
                params.set('q', searchQuery);
                if (petType !== 'all') params.set('petType', petType);
                if (selectedCategories.length > 0) params.set('categories', selectedCategories.join(','));
                if (selectedSubcategories.length > 0) params.set('subcategories', selectedSubcategories.join(','));
                if (selectedBrands.length > 0) params.set('brands', selectedBrands.join(','));
                navigate(`/search-results?${params.toString()}`);
                onClose();
              }
            }}
            style={{ width: '100%' }}
          >
            <input
              ref={searchRef}
              type="text"
              className="search-input-advanced"
              placeholder="Rechercher par nom, marque, description, poids, goût..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
            />
            {searchQuery && (
              <button
                type="button"
                className="clear-search-btn"
                onClick={() => setSearchQuery('')}
              >
                ✕
              </button>
            )}
          </form>
        </div>

        {/* Pet Type Tabs */}
        <div className="pet-type-tabs">
          {[
            { value: 'all', label: 'Tous' },
            { value: 'cat', label: '🐱 Chats' },
            { value: 'dog', label: '🐕 Chiens' },
            { value: 'rongeur', label: '🐭 Rongeurs' },
            { value: 'oiseaux', label: '🐦 Oiseaux' },
          ].map(tab => (
            <button
              key={tab.value}
              className={`pet-tab ${petType === tab.value ? 'active' : ''}`}
              onClick={() => setPetType(tab.value)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content Area */}
        <div className="search-content">
          {/* Left: Results */}
          <div className="search-results-section">
            <div className="results-header">
              <h3>
                {loading ? 'Recherche...' : `${filteredProducts.length} produit(s) trouvé(s)`}
              </h3>
              {(selectedCategories.length > 0 ||
                selectedSubcategories.length > 0 ||
                selectedBrands.length > 0 ||
                searchQuery) && (
                  <button className="clear-filters-btn" onClick={clearAllFilters}>
                    Effacer les filtres
                  </button>
                )}
            </div>

            <div className="products-grid">
              {loading ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Chargement...</p>
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="empty-state">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5">
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="m21 21-4.35-4.35"></path>
                  </svg>
                  <p>Aucun produit trouvé</p>
                  <p className="empty-subtitle">Essayez d'ajuster vos filtres ou votre recherche</p>
                </div>
              ) : (
                filteredProducts.map((product) => (
                  <div
                    key={product.id}
                    className="product-card-search"
                    onClick={() => handleProductClick(product.id)}
                  >
                    <div className="product-image-wrapper">
                      {product.image ? (
                        <img src={product.image} alt={product.name} />
                      ) : (
                        <div className="no-image">No image</div>
                      )}
                      {product.promo && (
                        <span className="promo-badge">
                          -{product.promo_percentage || 10}%
                        </span>
                      )}
                      {product.new && <span className="new-badge">Nouveau</span>}
                      {product.best && <span className="best-badge">⭐ Meilleur</span>}
                    </div>

                    <div className="product-info-search">
                      <p className="product-brand">{product.brand || 'Marque inconnue'}</p>
                      <h4 className="product-name">{product.name || 'Produit inconnu'}</h4>
                      <p className="product-description">
                        {product.description || 'Aucune description disponible'}
                      </p>

                      {product.stock_status && (
                        <div className="stock-status">
                          <span className={`stock-badge ${product.stock_status.toLowerCase()}`}>
                            {product.stock_status === 'in_stock' && '✓ En stock'}
                            {product.stock_status === 'out_of_stock' && '✕ Rupture de stock'}
                            {product.stock_status === 'low_stock' && '⚠ Stock limité'}
                          </span>
                        </div>
                      )}

                      <div className="product-price-section">
                        {product.promo && product.price_range ? (
                          <div className="price-with-promo">
                            <span className="old-price">
                              {new Intl.NumberFormat("fr-FR", {
                                minimumFractionDigits: 3,
                                maximumFractionDigits: 3,
                              }).format(parseFloat(product.price_range))}{" "}DT
                            </span>
                            <span className="current-price">
                              {new Intl.NumberFormat("fr-FR", {
                                minimumFractionDigits: 3,
                                maximumFractionDigits: 3,
                              }).format(
                                parseFloat(product.price_range) *
                                (1 - (product.promo_percentage || 10) / 100)
                              )}{" "}DT
                            </span>
                          </div>
                        ) : product.price_range ? (
                          <span className="current-price-solo">
                            {new Intl.NumberFormat("fr-FR", {
                              minimumFractionDigits: 3,
                              maximumFractionDigits: 3,
                            }).format(parseFloat(product.price_range))}{" "}DT
                          </span>
                        ) : (
                          <span className="current-price-solo">Prix indisponible</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Mobile Filter Toggle */}
          {window.innerWidth < 768 && (
            <div className="mobile-filters-toggle lg:hidden flex justify-end mb-2 px-4">
              <button
                onClick={() => setShowFilters(prev => !prev)}
                className="filter-icon-slide shadow-lg transition"
              >
                <Sliders size={20} />
              </button>
            </div>
          )}

          {/* Right: Filters */}
          {(showFilters || window.innerWidth > 768) && (
            <div className={`filters-sidebar ${showFilters ? 'visible' : 'hidden'}`}>
              <h3 className="sidebar-title">Filtres</h3>

              {/* Categories Filter */}
              {uniqueCategories.length > 0 && (
                <div className="filter-section">
                  <h4 className="filter-title">
                    Catégories ({uniqueCategories.length})
                  </h4>
                  <div className="filter-options">
                    {uniqueCategories.map((category) => (
                      <label key={category.id} className="filter-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedCategories.includes(category.id)}
                          onChange={() => toggleCategory(category.id)}
                        />
                        <span className="checkbox-custom"></span>
                        <span className="filter-label">{category.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Subcategories Filter */}
              {uniqueSubcategories.length > 0 && (
                <div className="filter-section">
                  <h4 className="filter-title">
                    Sous-catégories ({uniqueSubcategories.length})
                  </h4>
                  <div className="filter-options">
                    {uniqueSubcategories.map((subcategory) => (
                      <label key={subcategory.id} className="filter-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedSubcategories.includes(subcategory.id)}
                          onChange={() => toggleSubcategory(subcategory.id)}
                        />
                        <span className="checkbox-custom"></span>
                        <span className="filter-label">{subcategory.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Brands Filter */}
              {brands.length > 0 && (
                <div className="filter-section">
                  <h4 className="filter-title">Marques ({brands.length})</h4>
                  <div className="filter-options scrollable">
                    {brands.map((brand) => (
                      <label key={brand} className="filter-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedBrands.includes(brand)}
                          onChange={() => toggleBrand(brand)}
                        />
                        <span className="checkbox-custom"></span>
                        <span className="filter-label">{brand}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AdvancedSearch;